// Rocket Nozzle field ROWS (Space → Rocket Nozzle), split out of the old
// monolithic SpaceContent so the SAME controls render in two places without
// duplication:
//   • SpaceContent — the legacy single pane (feature-tree-windows flag OFF).
//   • The "Nozzle Parameters" / "Rocket Performance" floating windows — flag ON.
// Both read/write the shared rocketNozzleFieldsStore, so the value is one
// source of truth regardless of which surface is showing (mirrors
// TubeframeFields.tsx exactly).

import { useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { useHomepage } from '@/contexts/homepage-context';
import {
  computeRocketNozzleOutputs,
  fmt,
} from '@/lib/physics/rocketNozzleOutputs';
import { ft2El, inputFields } from './rocketNozzle';
import { useRocketNozzleFields } from './rocketNozzleFieldsStore';

// The "Nozzle Parameters" group — throat area, expansion ratio, chamber
// pressure, etc. (the editable inputs to the physics calculation).
export function NozzleParameterFields({ enabled }: { enabled: boolean }) {
  const { inputs, setField } = useRocketNozzleFields();
  const { bumpDomainParams } = useHomepage();

  return (
    <div className="space-y-2">
      {inputFields.map((field) => (
        <div key={field.key} className="flex items-center">
          <span className="min-w-0 flex-1 text-xs text-telekinesis-neutral-50">
            {field.label}
          </span>
          <Input
            type="number"
            step="any"
            disabled={!enabled}
            value={inputs[field.key]}
            onChange={(e) => {
              setField(field.key, e.target.value);
              bumpDomainParams();
            }}
            className="h-6 w-16 rounded-md px-1 text-right text-xs text-[#107d40]"
          />
          <span className="ml-1.5 w-14 text-right text-xs text-telekinesis-neutral-50">
            {field.unit}
          </span>
        </div>
      ))}
    </div>
  );
}

// The "Rocket Performance" group — read-only computed outputs (Mt/Me/Pe/Ue/
// thrust/Isp/...) derived from the shared inputs above.
export function RocketPerformanceFields() {
  const { inputs } = useRocketNozzleFields();
  const outputs = useMemo(() => computeRocketNozzleOutputs(inputs), [inputs]);

  const outputFields: {
    label: React.ReactNode;
    unit: React.ReactNode;
    value: string;
  }[] = [
    { label: <>{'Mt'}</>, unit: '[-]', value: outputs ? fmt(outputs.Mt) : '—' },
    { label: <>{'Me'}</>, unit: '[-]', value: outputs ? fmt(outputs.Me) : '—' },
    {
      label: <>{'Pe'}</>,
      unit: '[PSI]',
      value: outputs ? fmt(outputs.Pe_psi) : '—',
    },
    {
      label: <>{'Ue'}</>,
      unit: '[FPS]',
      value: outputs ? fmt(outputs.Ue_fps) : '—',
    },
    {
      label: 'Mass Flow',
      unit: '[lb/s]',
      value: outputs ? fmt(outputs.mdot_lbps) : '—',
    },
    {
      label: 'Thrust',
      unit: '[lbs]',
      value: outputs ? fmt(outputs.F_lbs) : '—',
    },
    { label: 'Isp', unit: '[s]', value: outputs ? fmt(outputs.Isp_s) : '—' },
    { label: 'NPR', unit: '[-]', value: outputs ? fmt(outputs.NPR) : '—' },
    {
      label: 'c*',
      unit: '[FPS]',
      value: outputs ? fmt(outputs.cstar_fps) : '—',
    },
    { label: 'CF', unit: '[-]', value: outputs ? fmt(outputs.CF) : '—' },
    { label: 'Ae', unit: ft2El, value: outputs ? fmt(outputs.Ae_ft2) : '—' },
  ];

  return (
    <div className="space-y-2">
      {outputFields.map((field, i) => (
        <div key={i} className="flex items-center">
          <span className="min-w-0 flex-1 text-xs text-telekinesis-neutral-50">
            {field.label}
          </span>
          <Input
            type="text"
            readOnly
            value={field.value}
            onChange={() => {}}
            className="h-6 w-16 cursor-default rounded-md px-1 text-right text-xs text-[#107d40] opacity-80"
          />
          <span className="ml-1.5 w-14 text-right text-xs text-telekinesis-neutral-50">
            {field.unit}
          </span>
        </div>
      ))}
    </div>
  );
}
